import { getDefinitiveNode } from '@nx-paeonia/utils-dom';
import { onChange$, onClick$, onEvent$, progressInstance } from '@nx-paeonia/utils-events';
import SwupPreloadPlugin from '@swup/preload-plugin';
import Swup from 'swup';
import { formatMoneyWithouthCurrency } from './helpers/locale.helper.js';

const swup = new Swup({
  linkSelector: 'a[href][data-swup]',
  plugins: [
    new SwupPreloadPlugin({
      preloadHoveredLinks: false,
      preloadVisibleLinks: false,
    }),
  ],
});

const openDisclaimers: string[] = [];

swup.hooks.on(
  'animation:out:start',
  () => {
    progressInstance.start();
    openDisclaimers.length = 0;

    for (const el of document.querySelectorAll<HTMLDetailsElement>('.filter-item')) {
      if (el.open) {
        openDisclaimers.push(el.id);
      }
    }
  },
  { before: true },
);

swup.hooks.on('animation:in:start', () => {
  for (const idx of openDisclaimers) {
    const el = document.querySelector<HTMLDetailsElement>(`#${idx}`);
    if (el) {
      el.open = true;
    }
  }

  const mediaMatcher = globalThis.matchMedia('(min-width: 640px)');
  if (!mediaMatcher.matches) {
    document.querySelector('.collection-filters:not(.collection-filters--mobile)')?.classList.add('hide');
  }

  progressInstance.done();
  preloadUrls();
  preloadVisible();
});

swup.hooks.on('visit:start', (visit) => {
  visit.scroll.reset = false;
});

document.addEventListener('DOMContentLoaded', () => {
  preloadUrls();
  preloadVisible();

  onChange$('[data-filter-url]', (_, el: HTMLElement): void => {
    const url = el.dataset.filterUrl;

    if (url !== undefined) {
      navigate(url);
    }
  });

  onChange$('.filters--price-range input', (): void => {
    const rangeElements = document.querySelectorAll<HTMLInputElement>('.filters--price-range input');

    const url = new URL(globalThis.location.href);

    for (const element of rangeElements) {
      const parameter = element.dataset.parameter;
      if (!parameter) return;

      const value = element.valueAsNumber;

      if (value && `${value}` !== element.max) {
        url.searchParams.set(parameter, `${value / 100}`);
      } else {
        url.searchParams.delete(parameter);
      }
    }

    navigate(url.toString());
  });

  onChange$('.filters--price-range input', (): void => {
    const rangeElements = document.querySelectorAll<HTMLInputElement>('.filters--price-range input');
    calculateSliderPercentages(Array.from(rangeElements));
    calculateSliderOutputs(Array.from(rangeElements));
  });

  onClick$('[data-action="toggle-collection-filters"]', (): void => {
    const overlay = getDefinitiveNode<HTMLDialogElement>('dialog.filter-overlay');
    getDefinitiveNode('.collection-filters:not(.collection-filters--mobile)')?.classList.add('hidden');

    overlay.querySelector('.hide')?.classList.remove('hide');
    overlay.show();
    overlay.removeAttribute('inert');
    overlay.setAttribute('aria-hidden', 'false');
  });

  onClick$('[data-action="close-collection-filters"]', (): void => {
    const overlay = getDefinitiveNode<HTMLDialogElement>('dialog.filter-overlay');
    getDefinitiveNode('.collection-filters:not(.collection-filters--mobile)')?.classList.add('hidden');

    overlay.close();
    overlay.setAttribute('inert', 'true');
    overlay.setAttribute('aria-hidden', 'true');
  });

  const mediaMatcher = globalThis.matchMedia('(min-width: 640px)');
  if (!mediaMatcher.matches) {
    document.querySelectorAll('.collection-filters')?.forEach((el): void => {
      el.classList.add('hide');
    });
  }

  mediaMatcher.addEventListener('change', (e): void => {
    if (e.matches) {
      document.querySelectorAll('.collection-filters')?.forEach((el): void => {
        el.classList.remove('hide');
      });
    } else {
      document.querySelectorAll('.collection-filters')?.forEach((el): void => {
        el.classList.add('hide');
      });
    }
  });
});

function calculateSliderPercentages(elements: HTMLInputElement[]): void {
  const minElement = elements.find((el) => el.dataset.type === 'min');
  const maxElement = elements.find((el) => el.dataset.type === 'max');

  if (!minElement || !maxElement) return;

  const min = minElement.min ? Number.parseInt(minElement.min, 10) : 0;
  const max = maxElement.max ? Number.parseInt(maxElement.max, 10) : 100;

  const minValue = minElement.valueAsNumber ? minElement.valueAsNumber : min;
  const maxValue = maxElement.valueAsNumber ? maxElement.valueAsNumber : max;

  const range = max - min;
  const minPercentage = ((minValue - min) / range) * 100;
  const maxPercentage = ((maxValue - min) / range) * 100;

  const rangeSlider = document.querySelector<HTMLElement>('.range-slider');

  if (rangeSlider) {
    rangeSlider.style.setProperty('--range-left-percent', `${minPercentage}%`);
    rangeSlider.style.setProperty('--range-right-percent', `${100 - maxPercentage}%`);
  }
}

function calculateSliderOutputs(elements: HTMLInputElement[]): void {
  const minElement = elements.find((el) => el.dataset.type === 'min');
  const maxElement = elements.find((el) => el.dataset.type === 'max');

  if (!minElement || !maxElement) return;

  const minValue = minElement.valueAsNumber ? minElement.valueAsNumber : 0;
  const maxValue = maxElement.valueAsNumber ? maxElement.valueAsNumber : 100;

  const minOutput = document.querySelector<HTMLOutputElement>('output[data-type="min"]');
  const maxOutput = document.querySelector<HTMLOutputElement>('output[data-type="max"]');

  if (minOutput) {
    minOutput.textContent = formatMoneyWithouthCurrency(minValue);
  }

  if (maxOutput) {
    maxOutput.textContent = formatMoneyWithouthCurrency(maxValue);
  }
}

function preloadUrls(): void {
  for (const el of document.querySelectorAll<HTMLInputElement>('.filter-item[open] [data-filter-url]')) {
    const url = el.dataset.filterUrl;

    if (typeof url === 'string') {
      swup.preload?.(url);
    }
  }
}

function preloadVisible(): void {
  onEvent$('.filter-item-collapsible', 'toggle', (): void => {
    preloadUrls();
  });
}

function navigate(url: string): void {
  swup.navigate(url);
}
